import { removePendingRoundFromSets } from '@heraion/shared';

import { blankSetFor, seedNextSet } from './loggerDraftHelpers';
import type { LoggerAction, LoggerDraft } from './loggerDraftTypes';

// Superset/circuit GROUP actions, split out of loggerNestedReducer.ts when
// EQ-1 pushed that file past the 500-line budget. Same seam and same shape
// as the noteEventReducer delegation already in that file: the parent
// narrows the union and hands the action over, behaviour unchanged.
//
// Why this cluster and not another: every case here is keyed on
// `entry.group?.id` and reasons about the group as a whole (all members
// gain a round, all members lose one, the group dissolves, a member moves
// within it). The rest of the parent switch reasons about ONE entry or ONE
// set. That line already existed — this only makes it a file boundary.

export type LoggerGroupAction = Extract<
  LoggerAction,
  {
    type:
      | 'add_round_to_group'
      | 'remove_round_from_group'
      | 'align_group_sets'
      | 'set_group'
      | 'split_group'
      | 'remove_group_member'
      | 'move_group_member'
      | 'toggle_group_effort';
  }
>;

export function loggerGroupReducer(
  state: LoggerDraft,
  action: LoggerGroupAction,
): LoggerDraft {
  switch (action.type) {
    case 'add_round_to_group': {
      // Symmetric "+1 set" across every member of a group (1.69.0
      // superset fusion). Mirrors the legacy web's group-add-set at
      // `7ae8e35e:apps/web/src/workout/WorkoutLog.tsx:3611-3640`: one click
      // appends one set per member so the rounds stay aligned. Each
      // member's new row picks up its own last-set's reps/weight via
      // seedNextSet — member A starts where A left off, member B
      // where B left off.
      return {
        ...state,
        entries: state.entries.map((entry) => {
          if (entry.group?.id !== action.groupId) return entry;
          return {
            ...entry,
            sets: [...entry.sets, seedNextSet(entry, action.defaultUnit)],
          };
        }),
      };
    }
    case 'remove_round_from_group': {
      // "−1 set" across every member of a group. The per-member safety rules
      // live in shared removePendingRoundFromSets (#387): a logged set is
      // never removed, and no member drops below 1 set. The superset menu
      // picks the round with pendingRoundIdx, from the same file.
      return {
        ...state,
        entries: state.entries.map((entry) => {
          if (entry.group?.id !== action.groupId) return entry;
          const sets = removePendingRoundFromSets(entry.sets, action.roundIdx);
          return sets === entry.sets ? entry : { ...entry, sets };
        }),
      };
    }
    case 'align_group_sets': {
      // Build the list of members in render order. Need ≥2 to align.
      const memberCounts: number[] = [];
      for (const e of state.entries) {
        if (e.group?.id === action.groupId) memberCounts.push(e.sets.length);
      }
      if (memberCounts.length < 2) return state;
      const target =
        action.mode === 'pad'
          ? Math.max(...memberCounts)
          : Math.max(1, Math.min(...memberCounts));
      if (memberCounts.every((c) => c === target)) return state;
      return {
        ...state,
        entries: state.entries.map((e) => {
          if (e.group?.id !== action.groupId) return e;
          if (e.sets.length === target) return e;
          if (e.sets.length < target) {
            // Pad. With `seed: 'empty'` (SS-3, superset creation) the added
            // rows are BLANK — the athlete fills them on purpose. Otherwise
            // (join prompt) re-derive each seed from the latest entry shape so
            // sequential rows pick up the carry-forward from the row we just
            // added (matches manual + Add round behaviour).
            let next = e;
            while (next.sets.length < target) {
              const seeded =
                action.seed === 'empty'
                  ? blankSetFor(next, action.defaultUnit, next.loadedSides)
                  : seedNextSet(next, action.defaultUnit);
              next = { ...next, sets: [...next.sets, seeded] };
            }
            return next;
          }
          // Trim — drop trailing sets. Pending tail is the typical
          // target; completed/PR sets sit at lower indices because the
          // user fills the WO top-down. Caller is responsible for
          // confirming the destructive action up the stack.
          return { ...e, sets: e.sets.slice(0, target) };
        }),
      };
    }
    case 'set_group': {
      const picked = action.entryIdxs.filter((i) => state.entries[i]);
      if (picked.length === 0) return state;
      return {
        ...state,
        entries: state.entries.map((e, i) =>
          picked.includes(i) ? { ...e, group: action.group } : e,
        ),
      };
    }
    case 'split_group': {
      const touched = state.entries.some((e) => e.group?.id === action.groupId);
      if (!touched) return state;
      return {
        ...state,
        entries: state.entries.map((e) =>
          e.group?.id === action.groupId ? { ...e, group: null } : e,
        ),
      };
    }
    case 'remove_group_member': {
      const target = state.entries[action.entryIdx];
      const gid = target?.group?.id;
      if (!gid) return state;
      const memberIdxs = state.entries
        .map((e, i) => (e.group?.id === gid ? i : -1))
        .filter((i) => i >= 0);
      const remaining = memberIdxs.filter((i) => i !== action.entryIdx);
      // Clear the removed member; if only one would be left, dissolve it too.
      const clear = new Set<number>([action.entryIdx]);
      if (remaining.length === 1) clear.add(remaining[0]!);
      return {
        ...state,
        entries: state.entries.map((e, i) =>
          clear.has(i) ? { ...e, group: null } : e,
        ),
      };
    }
    case 'move_group_member': {
      const target = state.entries[action.entryIdx];
      const gid = target?.group?.id;
      if (!gid) return state;
      const memberIdxs = state.entries
        .map((e, i) => (e.group?.id === gid ? i : -1))
        .filter((i) => i >= 0);
      const pos = memberIdxs.indexOf(action.entryIdx);
      const swapPos = action.direction === 'up' ? pos - 1 : pos + 1;
      if (swapPos < 0 || swapPos >= memberIdxs.length) return state;
      const a = action.entryIdx;
      const b = memberIdxs[swapPos]!;
      const entries = state.entries.slice();
      const tmp = entries[a]!;
      entries[a] = entries[b]!;
      entries[b] = tmp;
      return { ...state, entries };
    }
    case 'toggle_group_effort':
      return {
        ...state,
        entries: state.entries.map((e) =>
          e.group?.id === action.groupId && !e.isCardio
            ? { ...e, effortColumn: action.next }
            : e,
        ),
      };
  }
}
